import { useQueryStates } from 'nuqs';
import { useCallback } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { useContactSummaryQuery } from '@/hooks/queries/use-contact-summary-query';
import {
  isContactUnresolvedError,
  useResolveContactQuery,
} from '@/hooks/queries/use-resolve-contact-query';
import { contactDrawerParsers } from './contact-drawer-url-state';
import { useContactResolveRef } from './use-contact-resolve-ref';
import { useResolvedContactIdentityId } from './use-resolved-contact-identity-id';

export interface ContactDrawerError {
  /** True once resolution/summary settled in error with no other UI path. */
  isError: boolean;
  /** Refetches whichever of the two queries failed. */
  retry: () => void;
}

/**
 * Decides whether the drawer should show its explicit error state instead of a
 * blank shell.
 *
 * Error applies when the resolve query settles in error for an `affiliate`
 * entry, or a `creator` entry with no save path — and whenever the
 * summary query fails after a successful resolve. It does NOT apply to a
 * `creator` entry carrying a `contactSourceCreatorId` whose resolve failed with
 * the benign "no identity yet" outcome (a 404 / empty body): that is the
 * legitimate "unsaved discovery creator" case the rail's save prompt handles.
 * A real fault (5xx, network error) still surfaces the error state even for such
 * a creator, so a backend blip is not mistaken for an unsaved contact.
 *
 * Both queries share their cache keys with the drawer's other consumers, so
 * this hook adds no requests. While either query is still loading, `isError`
 * stays false and the drawer keeps its skeleton states.
 */
export const useContactDrawerError = (): ContactDrawerError => {
  const [{ contactEntry, contactSourceCreatorId }] = useQueryStates(contactDrawerParsers);
  const resolveRef = useContactResolveRef();
  const resolveQuery = useResolveContactQuery(resolveRef);
  const identityId = useResolvedContactIdentityId();
  const { user } = useAuth();
  const summaryQuery = useContactSummaryQuery(user?.currentBrandId, identityId);

  // The rail's save prompt only handles a creator entry that both carries a
  // source creator id AND resolved to a benign "no identity yet" (404 / empty).
  // A real fault (5xx, network) has no save path, so it must show the error.
  const hasSavePath =
    contactEntry === 'creator' &&
    !!contactSourceCreatorId &&
    resolveQuery.isError &&
    isContactUnresolvedError(resolveQuery.error);
  const isError = (resolveQuery.isError && !hasSavePath) || summaryQuery.isError;

  const { isError: resolveFailed, refetch: refetchResolve } = resolveQuery;
  const { isError: summaryFailed, refetch: refetchSummary } = summaryQuery;

  const retry = useCallback(() => {
    if (resolveFailed) void refetchResolve();
    if (summaryFailed) void refetchSummary();
  }, [resolveFailed, refetchResolve, summaryFailed, refetchSummary]);

  return { isError, retry };
};
